// V4-FRONT-ANY-USAGE batch 4 2026-05-28
/**
 * V4-ZERNIO-ANALYTICS — Onglet Analytics.
 *
 * Affiche les agrégats analytics du space courant (impressions / reach / likes /
 * comments / engagement_rate / post_count) avec breakdown par network.
 */
import { useState, useEffect, useCallback } from 'react';
import { BarChart3, RefreshCw, Loader2, TrendingUp, Eye, Heart, MessageCircle, Users } from 'lucide-react';
import { Button } from '../../ui/button';
import { TabHeader } from '../../common/TabHeader';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useAuth } from '../../../hooks/useAuth';
import { toastService } from '../../../services/toastService';
import { Logger } from '../../../utils/logger';
import { getChannelDisplayConfig } from '../../../utils/Constants';
import {
  zernioAnalyticsService,
  type SpaceAnalyticsResponse,
} from '../../../services/zernioAnalyticsService';

interface AnalyticsTabProps {
  onClose: () => void;
}

const PERIOD_OPTIONS = [7, 14, 30, 90] as const;
type Period = (typeof PERIOD_OPTIONS)[number];

// Source de vérité unique des labels réseau : `getChannelDisplayConfig`
// dans `utils/Constants.tsx`. Ne pas dupliquer.

function MetricCard({
  icon,
  label,
  value,
  hint,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  hint?: string;
}) {
  return (
    <div
      className="rounded-xl p-4 flex flex-col gap-2"
      style={{
        backgroundColor: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
      }}
    >
      <div className="flex items-center gap-2" style={{ color: 'var(--color-text-secondary)' }}>
        {icon}
        <span className="text-sm">{label}</span>
      </div>
      <div className="text-2xl font-semibold" style={{ color: 'var(--color-text-primary)' }}>
        {value}
      </div>
      {hint && (
        <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
          {hint}
        </div>
      )}
    </div>
  );
}

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(Math.round(n));
}

function formatPct(rate: number): string {
  return `${(rate * 100).toFixed(2)}%`;
}

export function AnalyticsTab({ onClose }: AnalyticsTabProps) {
  const { t } = useLanguage();
  const { currentSpace } = useAuth();
  const spaceId = currentSpace?.id;

  const [period, setPeriod] = useState<Period>(30);
  const [data, setData] = useState<SpaceAnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ta = t.analyticsTab;

  const load = useCallback(async () => {
    if (!spaceId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await zernioAnalyticsService.getSpaceAnalytics(spaceId, period);
      setData(result);
    } catch (err) {
      Logger.error('[AnalyticsTab] Failed to load space analytics', err);
      setError(ta.loadError);
      toastService.error(ta.loadError);
    } finally {
      setLoading(false);
    }
  }, [spaceId, period, ta.loadError]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="flex flex-col h-full">
      {/* Header — coque partagée V4-FRONT-HEADERS-UNIFORM */}
      <TabHeader
        icon={<BarChart3 className="w-5 h-5" style={{ color: 'var(--color-primary)' }} />}
        title={t.menu.analytics}
        onClose={onClose}
        actions={
          <Button
            variant="ghost"
            size="icon"
            onClick={load}
            disabled={loading}
            title={ta.refresh}
            className="rounded-full"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        }
      />

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Period selector */}
        <div className="flex items-center gap-2">
          <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            {ta.period}
          </span>
          {PERIOD_OPTIONS.map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className="px-3 py-1 rounded-full text-sm transition-colors"
              style={{
                backgroundColor:
                  p === period
                    ? 'var(--color-primary)'
                    : 'var(--color-surface)',
                color:
                  p === period
                    ? 'var(--color-text-light)'
                    : 'var(--color-text-secondary)',
                border: '1px solid var(--color-border)',
              }}
            >
              {p}{ta.days}
            </button>
          ))}
        </div>

        {/* States */}
        {!spaceId && (
          <div className="text-center py-12" style={{ color: 'var(--color-text-muted)' }}>
            {ta.noSpace}
          </div>
        )}

        {error && (
          <div
            className="rounded-lg p-3 text-sm"
            style={{
              backgroundColor: 'var(--color-danger-light, rgba(239, 68, 68, 0.1))',
              border: '1px solid var(--color-danger, #ef4444)',
              color: 'var(--color-danger, #ef4444)',
            }}
          >
            {error}
          </div>
        )}

        {loading && !data && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--color-primary)' }} />
          </div>
        )}

        {data && (
          <>
            {/* Global metrics grid */}
            <div className="grid grid-cols-2 gap-3">
              <MetricCard icon={<Eye className="w-4 h-4" />} label={ta.impressions} value={formatNumber(data.impressions)} />
              <MetricCard icon={<Users className="w-4 h-4" />} label={ta.reach} value={formatNumber(data.reach)} />
              <MetricCard icon={<Heart className="w-4 h-4" />} label={ta.likes} value={formatNumber(data.likes)} />
              <MetricCard icon={<MessageCircle className="w-4 h-4" />} label={ta.comments} value={formatNumber(data.comments_count)} />
              <MetricCard icon={<TrendingUp className="w-4 h-4" />} label={ta.engagement} value={formatPct(data.engagement_rate)} />
              <MetricCard
                icon={<BarChart3 className="w-4 h-4" />}
                label={ta.publications}
                value={data.post_count}
                hint={(ta.onPeriod ?? '').replace('{days}', String(data.period_days))}
              />
            </div>

            {/* Breakdown by network */}
            {data.by_network && data.by_network.length > 0 && (
              <div className="space-y-2">
                <h3
                  className="text-sm font-semibold mt-4"
                  style={{ color: 'var(--color-text-primary)' }}
                >
                  {ta.byNetwork}
                </h3>
                <div className="space-y-2">
                  {data.by_network.map((b) => (
                    <div
                      key={b.network}
                      className="rounded-xl p-3 flex items-center justify-between"
                      style={{
                        backgroundColor: 'var(--color-surface)',
                        border: '1px solid var(--color-border)',
                      }}
                    >
                      <div className="flex flex-col">
                        <span style={{ color: 'var(--color-text-primary)' }}>
                          {getChannelDisplayConfig(b.network).label}
                        </span>
                        <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                          {(ta.postsCount ?? '').replace('{count}', String(b.post_count)).replace('{rate}', formatPct(b.engagement_rate))}
                        </span>
                      </div>
                      <div className="flex gap-4 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                        <div className="flex flex-col items-end">
                          <span>{formatNumber(b.impressions)}</span>
                          <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>{ta.impLabel}</span>
                        </div>
                        <div className="flex flex-col items-end">
                          <span>{formatNumber(b.likes)}</span>
                          <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>{ta.likesLabel}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {data.post_count === 0 && (
              <div
                className="rounded-lg p-4 text-sm text-center"
                style={{
                  backgroundColor: 'var(--color-surface)',
                  border: '1px solid var(--color-border)',
                  color: 'var(--color-text-secondary)',
                }}
              >
                {ta.noPublications}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
